import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stepper,
  Step,
  StepLabel,
  Button,
  Alert,
  AlertTitle,
  CircularProgress,
  Chip,
  Grid,
  Divider,
} from '@mui/material';
import {
  MergeType as MergeTypeIcon,
  Search as SearchIcon,
  RuleFolder as ReviewIcon,
  CheckCircle as ConfirmIcon,
} from '@mui/icons-material';

import SchoolSelectionStep from './merge_workflow/SchoolSelectionStep';
import MergeReviewStep from './merge_workflow/MergeReviewStep';
import MergeConfirmStep from './merge_workflow/MergeConfirmStep';

const MergeSchoolWorkflow = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [isProcessing, setIsProcessing] = useState(false);
  const [workflowData, setWorkflowData] = useState({
    sourceSchool: null,
    survivorSchool: null,
    previewData: null,
    mergeResult: null,
  });

  const steps = [
    {
      label: 'Select Schools',
      description: 'Choose source and survivor',
      icon: <SearchIcon />,
      component: SchoolSelectionStep,
    },
    {
      label: 'Review & Validate',
      description: 'Confirm data compatibility',
      icon: <ReviewIcon />,
      component: MergeReviewStep,
    },
    {
      label: 'Confirm & Merge',
      description: 'Execute the merge',
      icon: <ConfirmIcon />,
      component: MergeConfirmStep,
    },
  ];

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  const handleReset = () => {
    setActiveStep(0);
    setIsProcessing(false);
    setWorkflowData({
      sourceSchool: null,
      survivorSchool: null,
      previewData: null,
      mergeResult: null,
    });
  };

  const updateWorkflowData = (stepData) => {
    setWorkflowData((prev) => ({ ...prev, ...stepData }));
  };

  const isStepValid = (stepIndex) => {
    switch (stepIndex) {
      case 0:
        return (
          workflowData.sourceSchool !== null &&
          workflowData.survivorSchool !== null &&
          workflowData.sourceSchool.uuid !== workflowData.survivorSchool.uuid
        );
      case 1:
        return (
          workflowData.previewData !== null &&
          workflowData.previewData.conflicts.length === 0
        );
      case 2:
        return workflowData.mergeResult !== null && workflowData.mergeResult.success;
      default:
        return false;
    }
  };

  const getCurrentStepComponent = () => {
    const StepComponent = steps[activeStep].component;
    return (
      <StepComponent
        workflowData={workflowData}
        onDataUpdate={updateWorkflowData}
        onNext={handleNext}
        onBack={handleBack}
        isValid={isStepValid(activeStep)}
        isProcessing={isProcessing}
        setIsProcessing={setIsProcessing}
      />
    );
  };

  const isLastStep = activeStep === steps.length - 1;
  const mergeComplete = isLastStep && workflowData.mergeResult?.success;

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', p: 3 }}>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <MergeTypeIcon color="error" sx={{ fontSize: 32 }} />
            <Typography variant="h4" component="h1">
              Merge Schools
            </Typography>
            {isProcessing && (
              <Chip
                label="Processing..."
                color="primary"
                icon={<CircularProgress size={16} />}
              />
            )}
          </Box>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            Consolidate two schools that represent the same physical school across different NCES ID eras
            into a single unified record with complete enrollment history.
          </Typography>

          <Alert severity="warning" sx={{ mb: 0 }}>
            <AlertTitle>Destructive Operation</AlertTitle>
            The source (historical) school will be <strong>permanently deactivated</strong> and all of
            its enrollment, FRL, grades, directory, and name records will be transferred to the
            survivor school. This cannot be undone.
          </Alert>
        </CardContent>
      </Card>

      {/* Progress Stepper */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((step, index) => (
              <Step key={step.label} completed={index < activeStep}>
                <StepLabel
                  StepIconComponent={({ active, completed }) => (
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: completed
                          ? 'success.main'
                          : active
                          ? 'error.main'
                          : 'grey.300',
                        color: completed || active ? 'white' : 'grey.600',
                      }}
                    >
                      {step.icon}
                    </Box>
                  )}
                >
                  <Typography variant="subtitle2">{step.label}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {step.description}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </CardContent>
      </Card>

      {/* Step Content */}
      <Card>
        <CardContent>{getCurrentStepComponent()}</CardContent>
      </Card>

      {/* Navigation Controls */}
      <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2 }}>
        <Button
          color="inherit"
          disabled={activeStep === 0 || isProcessing || mergeComplete}
          onClick={handleBack}
          sx={{ mr: 1 }}
        >
          Back
        </Button>
        <Box sx={{ flex: '1 1 auto' }} />

        {mergeComplete ? (
          <Button variant="outlined" onClick={handleReset}>
            Merge Another School
          </Button>
        ) : isLastStep ? null : (
          <Button
            variant="contained"
            color={activeStep === steps.length - 2 ? 'error' : 'primary'}
            onClick={handleNext}
            disabled={!isStepValid(activeStep) || isProcessing}
          >
            {activeStep === steps.length - 2 ? 'Continue to Merge' : 'Next'}
          </Button>
        )}
      </Box>

      {/* Workflow Summary (when both schools are selected) */}
      {workflowData.sourceSchool && workflowData.survivorSchool && (
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Merge Summary
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Source (will be deactivated)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {workflowData.sourceSchool.display_name || workflowData.sourceSchool.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  NCES: {workflowData.sourceSchool.ncessch}
                </Typography>
                {workflowData.previewData && (
                  <Typography variant="body2" color="text.secondary">
                    Years: {workflowData.previewData.year_ranges.source_enrollment.join(', ')}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Survivor (keeps its NCES ID)
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {workflowData.survivorSchool.display_name || workflowData.survivorSchool.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  NCES: {workflowData.survivorSchool.ncessch}
                </Typography>
                {workflowData.previewData && (
                  <Typography variant="body2" color="text.secondary">
                    Years: {workflowData.previewData.year_ranges.survivor_enrollment.join(', ')}
                  </Typography>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default MergeSchoolWorkflow;
